/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DxpServiceId } from '@squiz/dxp-auth';
import { useObservable } from '@squiz/dxp-console-support';
import {
  EntityTenantSetting,
  EntityTenantSettings,
  getTenantSettingsService,
} from 'apps/console-core/src/Services/tenant-settings';
import { useMemo } from 'react';
import { Observable } from 'rxjs';
import { JsonObject } from 'type-fest';
import { DamIntegrationPublic, DamIntegrationsPublic } from '../../types/DamIntegrationsPublic/DamIntegrationsPublic';

/**
 * The result expected of the useDamIntegrations hook.
 */
export type DamIntegrationsResult = [DamIntegrationData | null, JsonObject | null];

/**
 * The data returned by the useDamIntegrations hook.
 */
export interface DamIntegrationData {
  integrations: Array<DamIntegrationPublic>;
  tenantSetting?: EntityTenantSetting<DamIntegrationsPublic>;
}

/**
 * React hook to get a list of public DAM integrations from the tenant settings service.
 */
export function useDamIntegrations(): DamIntegrationsResult {
  const tenantSettingsService = getTenantSettingsService();

  const integrations$ = useMemo((): Observable<EntityTenantSettings<DamIntegrationsPublic> | null> => {
    return tenantSettingsService.getSettingsList<DamIntegrationsPublic>({
      schemaId: 'dam-integrations-public',
      serviceId: DxpServiceId.dxpDAMIntegrations,
      showHiddenValues: true,
    });
  }, [tenantSettingsService]);

  const [integrations, error] = useObservable(integrations$, [tenantSettingsService]);

  return useMemo((): DamIntegrationsResult => {
    if (integrations && integrations.data) {
      const tenantSetting = integrations.data.length ? integrations.data[0] : undefined;

      // If multiple tenant settings exist with the 'dam-integrations-public' schema,
      // combine the integrations into a single array
      const damIntegrations = integrations.data.reduce(
        (result: Array<DamIntegrationPublic>, setting: EntityTenantSetting<DamIntegrationsPublic>) => {
          result.push(...setting.value.integrations);
          return result;
        },
        [],
      );

      return [{ integrations: damIntegrations, tenantSetting }, error as JsonObject];
    }

    return [null, error as JsonObject];
  }, [error, integrations]);
}
